import type { ReactNode } from 'react';

import { useSidekickDockInset } from '~/components/SidekickDockMount';

import useStyles from './GlobalSnackbarStack.styles';
import { useMeasuredHeight } from './useMeasuredHeight';

export type GlobalSnackbarStackProps = {
  /** Global bottom-right widgets to stack (e.g. progress snackbars). */
  children: ReactNode;
};

/**
 * Shared fixed container for the globally-mounted bottom-right widgets
 * (vehicle-import and device-activation progress). It stacks them in a flex
 * column so they never overlap or hide one another, regardless of z-index.
 *
 * Children render directly (no per-child wrapper), so a widget that returns
 * null leaves no placeholder flex item and the `gap` never adds phantom
 * spacing. Pointer events are handled in CSS: the container is click-through
 * over the empty corner, and each rendered widget re-enables them.
 *
 * The docked assistant claims the same corner without joining the stack, so
 * the stack moves clear of whichever edge it is holding. When the assistant
 * leaves no corner at all — its drawer at mobile width, or its expanded card
 * on a viewport with room for only one of them — the stack goes unpainted
 * until the corner is free again, rather than drawing on top of it.
 *
 * Unpainted, not unmounted: the widgets own long-lived progress state (the
 * vehicle-import poller carries an exponential backoff across renders), so
 * tearing them down would restart the work they are reporting on.
 *
 * Whether the corner has room is answered from the stack's measured height
 * rather than from what its widgets are expected to be. They expand and
 * collapse on their own, render one row per in-flight batch, and swap to
 * taller terminal cards when the work finishes, so their footprint is only
 * knowable once drawn.
 */
export const GlobalSnackbarStack = ({
  children,
}: GlobalSnackbarStackProps): JSX.Element => {
  const { ref, heightPx } = useMeasuredHeight();
  const { bottomPx, rightPx, leavesNoRoom } = useSidekickDockInset(heightPx);
  const { classes } = useStyles({
    dockInsetBottomPx: bottomPx,
    dockInsetRightPx: rightPx,
    isUnpainted: leavesNoRoom,
  });

  return (
    <div
      ref={ref}
      className={classes.stack}
      data-testid="global-snackbar-stack"
    >
      {children}
    </div>
  );
};

export default GlobalSnackbarStack;
